import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

/**
 * XLS export for the JD & Skills page (added 2026-09-25).
 *
 * Mirrors exactly what is on screen: it reads the rendered <table>s inside
 * `root` rather than re-deriving the data, so whichever view is showing
 * (AI Summary / Job Description / Skills Matrix, any filters) is what lands
 * in the file - including colspan/rowspan merges, header bolding and the
 * skills matrix cell colours.
 *
 * Tables opt in with `data-xls-title="..."` (written as a heading row above
 * the table). The export root's `data-xls-filename` / `data-xls-sheet`
 * attributes name the file and sheet.
 */

const BLOCK_TAGS = new Set(["DIV", "P", "LI", "UL", "OL", "H1", "H2", "H3", "H4", "H5", "H6", "TR"]);

// Cell text with line breaks where the screen has them (sections, bullet
// points, the two-line role headers). innerText can't be used on its own -
// it drops list bullets.
function cellText(el) {
  const lines = [];
  let current = "";
  const flush = () => {
    const t = current.replace(/\s+/g, " ").trim();
    if (t) lines.push(t);
    current = "";
  };
  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      current += node.textContent;
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (node.tagName === "BR") {
      flush();
      return;
    }
    const block = BLOCK_TAGS.has(node.tagName);
    if (block) flush();
    if (node.tagName === "LI") current += "• ";
    node.childNodes.forEach(walk);
    if (block) flush();
  };
  walk(el);
  flush();
  return lines.join("\n");
}

// "rgb(r, g, b)" / "rgba(r, g, b, a)" -> "FFRRGGBB", or null when fully
// transparent.
function toArgb(cssColor) {
  const m = cssColor && cssColor.match(/rgba?\(\s*(\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\s*\)/);
  if (!m) return null;
  if (m[4] !== undefined && parseFloat(m[4]) === 0) return null;
  const hex = [m[1], m[2], m[3]].map((n) => Number(n).toString(16).padStart(2, "0")).join("");
  return `FF${hex.toUpperCase()}`;
}

const THIN = { style: "thin", color: { argb: "FFD0D4DE" } };
const BORDER = { top: THIN, left: THIN, bottom: THIN, right: THIN };

function writeTable(ws, table, startRow, colWidths, merged) {
  // Occupancy grid so rowspan/colspan cells land in the right Excel columns.
  const taken = new Set();
  let rowNum = startRow;

  table.querySelectorAll("tr").forEach((tr) => {
    let col = 1;
    tr.querySelectorAll(":scope > th, :scope > td").forEach((cell) => {
      while (taken.has(`${rowNum}:${col}`)) col++;
      const colSpan = cell.colSpan || 1;
      const rowSpan = cell.rowSpan || 1;
      const text = cellText(cell);

      const xl = ws.getCell(rowNum, col);
      xl.value = text;
      xl.alignment = { vertical: "top", wrapText: true };
      xl.border = BORDER;
      // Colours copied from the screen, so dark header cells keep their
      // white text instead of turning into black-on-navy.
      const style = window.getComputedStyle(cell);
      const fill = toArgb(style.backgroundColor);
      const fontColor = toArgb(style.color);
      xl.font = { bold: cell.tagName === "TH", ...(fontColor ? { color: { argb: fontColor } } : {}) };
      if (fill && fill !== "FFFFFFFF") xl.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fill } };

      for (let r = 0; r < rowSpan; r++) {
        for (let c = 0; c < colSpan; c++) taken.add(`${rowNum + r}:${col + c}`);
      }
      if (colSpan > 1 || rowSpan > 1) {
        ws.mergeCells(rowNum, col, rowNum + rowSpan - 1, col + colSpan - 1);
        // Excel won't auto-size a row to a merged, wrapped cell (the AI
        // Summary paragraph) - sized in exportTablesToXls once widths are known.
        if (rowSpan === 1 && text) merged.push({ row: rowNum, col, colSpan, text });
      }
      if (colSpan === 1) {
        const longest = Math.max(0, ...text.split("\n").map((l) => l.length));
        colWidths[col] = Math.max(colWidths[col] || 12, Math.min(60, longest + 2));
      }
      col += colSpan;
    });
    rowNum++;
  });

  let lastRow = rowNum - 1;
  taken.forEach((key) => {
    const r = Number(key.split(":")[0]);
    if (r > lastRow) lastRow = r;
  });
  return lastRow;
}

/** True when `root` currently shows at least one exportable table. */
export function hasExportableTable(root) {
  return Boolean(root && root.querySelector("table[data-xls-title]"));
}

export async function exportTablesToXls(root) {
  const tables = root ? Array.from(root.querySelectorAll("table[data-xls-title]")) : [];
  if (!tables.length) return;

  const source = root.querySelector("[data-xls-filename]");
  const fileName = source?.dataset.xlsFilename || "Minerva-Export";
  const sheetName = (source?.dataset.xlsSheet || "Export").slice(0, 31);

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(sheetName);
  const colWidths = {};
  const merged = [];
  let row = 1;

  tables.forEach((table) => {
    const heading = ws.getCell(row, 1);
    heading.value = table.dataset.xlsTitle;
    heading.font = { bold: true, size: 13 };
    row += 1;
    row = writeTable(ws, table, row, colWidths, merged) + 2;
  });

  Object.entries(colWidths).forEach(([col, width]) => {
    ws.getColumn(Number(col)).width = width;
  });

  merged.forEach(({ row: r, col, colSpan, text }) => {
    let width = 0;
    for (let c = col; c < col + colSpan; c++) width += colWidths[c] || 12;
    const lines = text.split("\n").reduce((n, line) => n + Math.max(1, Math.ceil(line.length / width)), 0);
    const height = lines * 15 + 4;
    if (height > (ws.getRow(r).height || 15)) ws.getRow(r).height = height;
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, `${fileName.replace(/[\\/:*?"<>|]+/g, "").replace(/\s+/g, "-")}.xlsx`);
}
